import { createAsyncThunk } from '@reduxjs/toolkit';
import { UserDefinedTemplateApiResponse, UserDefinedTemplate } from './types';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

// Define the API response structure
interface ApiResponse {
  content: UserDefinedTemplateApiResponse[];
  last: boolean;
  page: number;
  size: number;
  totalCount: number;
  totalPages: number;
}


// Define the parameters for fetching templates
export interface FetchTemplatesParams {
  orgId: string;
  page?: number;
  size?: number;
  name?: string;
  category?: string;
}

// Define the return type with pagination metadata
export interface FetchTemplatesResult {
  content: UserDefinedTemplate[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export const fetchUserDefinedTemplates = createAsyncThunk<
  FetchTemplatesResult,
  FetchTemplatesParams,
  { rejectValue: string }
>('templates/fetchUserDefinedTemplates', async ({ orgId, page, size, name = '', category = '' }, { rejectWithValue }) => {
  try {
    const { token, tenantId, userId } = getAuthContext();

    if (!token) {
      return rejectWithValue('Authentication token not available');
    }

    let url = `${BASE_URL}/v1/templates/user-define?orgId=${orgId}`;

    if (page !== undefined) {
      url += `&page=${page}`;
    }
    if (size !== undefined) {
      url += `&size=${size}`;
    }
    if (name && name.trim()) {
      url += `&name=${encodeURIComponent(name.trim())}`;
    }
    if (category && category.trim()) {
      url += `&metadata.category=${encodeURIComponent(category.trim())}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenantId || '',
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      // console.error('API Error:', response.status, errorText);
      return rejectWithValue(
        `Failed to fetch user templates: ${response.status}`
      );
    }

    const apiResponse: ApiResponse = await response.json();
    const data = apiResponse.content;

    const templates: UserDefinedTemplate[] = data
      .map((item): UserDefinedTemplate | null => {
        let html = '';
        let css = item.styles?.trim() || '';

        if (item.blocks && Array.isArray(item.blocks)) {
          const rawBlock = item.blocks.find((b) => b.type === 'rawHtml');
          if (rawBlock?.text?.trim()) {
            html = rawBlock.text
              .trim()
              .replace(/\\n/g, '\n')
              .replace(/\\"/g, '"');
          }
        }

        if (!html && item.blocks && Array.isArray(item.blocks)) {
          html = item.blocks
            .map((block) => {
              switch (block.type) {
                case 'header':
                  return `<h1>${block.text}</h1>`;
                case 'paragraph':
                  return `<p>${block.text}</p>`;
                case 'image':
                  return `<img src="${block.text}" alt="Image" />`;
                case 'button':
                  const [text, href] = block.text.split('|');
                  return `<a href="${href || '#'}" class="button">${text || 'Button'
                    }</a>`;
                case 'divider':
                  return `<hr class="divider" />`;
                default:
                  return '';
              }
            })
            .join('');
        }

        if (!html) {
          console.warn('No HTML found for user template:', item.id, item.name);
          return null;
        }

        return {
          id: item.id,
          name: item.name,
          description: item.description,
          html,
          css: css.replace(/\\n/g, '\n').replace(/\\"/g, '"'),
          createdAt: item.createdAt || new Date().toISOString(),
          updatedAt: item.updatedAt,
          status: item.status,
          version: item.version,
          folderId: item.folderId,
          category: item.metadata?.category || 'Uncategorized',
          tags: item.metadata?.tags || [],
          createdBy: item.createdBy,
          isPrebuilt: false,
          isUserDefined: true,
          thumbnail: item.thumbnailUrl,
          blocks: item.blocks,
        };
      })
      .filter((t): t is UserDefinedTemplate => t !== null);

    return {
      content: templates,
      totalCount: apiResponse.totalCount,
      totalPages: apiResponse.totalPages,
      currentPage: apiResponse.page,
      pageSize: apiResponse.size,
    };
  } catch (err: any) {
    // console.error('Network error:', err);
    return rejectWithValue(err.message || 'Network error');
  }
});

// Updated function to fetch category count with search term
export const fetchCategoryCount = async (
  orgId: string,
  category: string,
  type: 'user' | 'prebuilt',
  searchTerm: string = ''
): Promise<number> => {
  try {
    const { token, tenantId, userId } = getAuthContext();

    if (!token) {
      return 0;
    }

    const endpoint = type === 'user'
      ? `${BASE_URL}/v1/templates/user-define`
      : `${BASE_URL}/v1/templates/pre-build`;

    let url = `${endpoint}?orgId=${orgId}&metadata.category=${encodeURIComponent(category)}`;
    if (searchTerm && searchTerm.trim()) {
      url += `&name=${encodeURIComponent(searchTerm.trim())}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenantId || '',
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // console.error('Failed to fetch category count:', response.status);
      return 0;
    }

    const data = await response.json();
    return data.totalCount || 0;
  } catch (err) {
    // console.error('Error fetching category count:', err);
    return 0;
  }
};

export default fetchUserDefinedTemplates;